// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ShieldCheck, UserPlus, Users, AlertCircle, Search, CheckCircle2, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PersonAvatar } from '@/components/ui/person-avatar'
import AddProviderModal from '@/components/AddProviderModal'
import MemberActions, { type ActionResult } from '@/components/MemberActions'
import { listMembers, type OrgMember } from '@/lib/api'
import { useAuth } from '@/context/auth-context'

type Status = 'loading' | 'done' | 'error'

// org_admin and operators (qh_admin/platform_superadmin) administer the org.
function isAdminRole(role: string): boolean {
  return role === 'org_admin' || role === 'qh_admin' || role === 'platform_superadmin'
}

// Humanize a snake_case role/status (e.g. "org_admin" → "Org admin").
function humanize(s: string): string {
  if (!s) return ''
  const spaced = s.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

const STATUS_STYLE: Record<string, string> = {
  active: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  invited: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  disabled: 'bg-muted text-muted-foreground',
}

function ProviderRow({ provider, onChanged, onResult }: {
  provider: OrgMember
  onChanged: () => void
  onResult: (r: ActionResult) => void
}) {
  const admin = isAdminRole(provider.role)
  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-border last:border-0 hover:bg-secondary/40 transition-colors">
      <PersonAvatar seed={provider.uid || provider.email} size={36} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{provider.email}</p>
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          {admin && <ShieldCheck className="h-3 w-3 text-[#02C7C3]" />}
          {humanize(provider.role)}
        </p>
      </div>
      {provider.status && (
        <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_STYLE[provider.status] ?? 'bg-secondary text-muted-foreground'}`}>
          {humanize(provider.status)}
        </span>
      )}
      <MemberActions
        member={{ id: provider.id, role: provider.role, status: provider.status }}
        onChanged={onChanged}
        onResult={onResult}
      />
    </div>
  )
}

function ShimmerRow() {
  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-border last:border-0">
      <div className="h-9 w-9 rounded-full shimmer shrink-0" />
      <div className="flex-1 flex flex-col gap-1.5">
        <div className="h-3.5 w-48 rounded shimmer" />
        <div className="h-3 w-20 rounded shimmer" />
      </div>
      <div className="h-5 w-16 rounded-full shimmer" />
    </div>
  )
}

export default function ProvidersPage() {
  const { t } = useTranslation()
  const { actingAsOrg, isOperator } = useAuth()
  const [addOpen, setAddOpen] = useState(false)
  const [providers, setProviders] = useState<OrgMember[]>([])
  const [status, setStatus] = useState<Status>('loading')
  const [errorMsg, setErrorMsg] = useState('')
  const [search, setSearch] = useState('')
  const [actionResult, setActionResult] = useState<ActionResult | null>(null)

  // Operators must pick an org first — the roster is org-scoped (would 404/empty).
  const needsOrgSelection = isOperator && !actingAsOrg

  const fetchProviders = useCallback(async () => {
    if (needsOrgSelection) {
      setProviders([])
      setStatus('done')
      return
    }
    setStatus('loading')
    setErrorMsg('')
    try {
      // Strictly org-scoped members — the same set the row actions (remove/role/resend)
      // can act on. (Previously read from /v1/dashboard, which for operators listed
      // cross-org members that 404'd on remove.)
      setProviders(await listMembers())
      setStatus('done')
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Unknown error')
      setStatus('error')
    }
    // actingAsOrg keys the fetch so an operator switching orgs reloads the roster.
  }, [actingAsOrg?.id, needsOrgSelection])

  useEffect(() => { void fetchProviders() }, [fetchProviders])

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return providers
    return providers.filter(
      (p) => p.email.toLowerCase().includes(q) || humanize(p.role).toLowerCase().includes(q),
    )
  }, [providers, search])

  return (
    <div className="flex flex-col gap-6">
      {/* Gradient hero header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#012031] via-[#033C85] to-[#006EF9] animate-fade-up">
        <div className="absolute -bottom-16 -right-16 w-[220px] h-[220px] rounded-full bg-[#02C7C3]/20 blur-3xl pointer-events-none" />
        <div className="absolute top-1/3 -left-12 w-[160px] h-[160px] rounded-full bg-white/5 blur-2xl pointer-events-none" />
        <div className="relative z-10 px-6 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-white/15 border border-white/25 flex items-center justify-center shrink-0">
              <ShieldCheck className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white leading-tight">{t('providers.pageTitle')}</h1>
              <p className="text-white/65 text-sm mt-1">{t('providers.pageSubtitle')}</p>
            </div>
          </div>
          {!needsOrgSelection && (
            <Button
              onClick={() => setAddOpen(true)}
              className="w-full sm:w-auto bg-white text-[#033C85] hover:bg-white/90 border-0 font-semibold shadow-lg"
            >
              <UserPlus className="h-4 w-4" />
              {t('providers.addProvider')}
            </Button>
          )}
        </div>
      </div>

      {needsOrgSelection ? (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <div className="h-14 w-14 rounded-2xl bg-secondary flex items-center justify-center">
            <Users className="h-7 w-7 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-foreground">{t('dashboard.selectOrgTitle')}</p>
          <p className="text-xs text-muted-foreground max-w-xs">{t('dashboard.selectOrgBody')}</p>
        </div>
      ) : status === 'error' ? (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <div className="h-14 w-14 rounded-2xl bg-destructive/10 flex items-center justify-center">
            <AlertCircle className="h-7 w-7 text-destructive" />
          </div>
          <p className="text-sm font-medium text-foreground">{t('providers.loadError')}</p>
          <p className="text-xs text-muted-foreground max-w-xs">{errorMsg}</p>
          <Button variant="outline" size="sm" onClick={() => void fetchProviders()} className="mt-1">
            {t('providers.retry')}
          </Button>
        </div>
      ) : (
        <>
          {/* Transient result of a member action (resend / role / remove). */}
          {actionResult && (
            <div className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl border text-sm ${actionResult.kind === 'success' ? 'bg-accent/10 border-accent/25 text-foreground' : 'bg-destructive/8 border-destructive/20 text-destructive'}`}>
              {actionResult.kind === 'success' ? <CheckCircle2 className="h-4 w-4 shrink-0 text-accent" /> : <XCircle className="h-4 w-4 shrink-0" />}
              <span>{actionResult.text}</span>
              <button onClick={() => setActionResult(null)} className="ml-auto text-xs text-muted-foreground hover:text-foreground">✕</button>
            </div>
          )}

          {/* Toolbar: search + count */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                type="search"
                className="pl-10"
                placeholder={t('providers.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {status === 'done' && (
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                {t('providers.memberCount', { count: providers.length })}
              </span>
            )}
          </div>

          {/* List */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            {status === 'loading' ? (
              Array.from({ length: 6 }).map((_, i) => <ShimmerRow key={i} />)
            ) : providers.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-16 text-center">
                <div className="h-14 w-14 rounded-2xl bg-secondary flex items-center justify-center">
                  <Users className="h-7 w-7 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium text-foreground">{t('providers.empty')}</p>
                <p className="text-xs text-muted-foreground max-w-xs">{t('providers.emptyBody')}</p>
              </div>
            ) : visible.length === 0 ? (
              <p className="py-12 text-center text-sm text-muted-foreground">{t('providers.noResults')}</p>
            ) : (
              visible.map((p) => (
                <ProviderRow
                  key={p.id || p.uid || p.email}
                  provider={p}
                  onChanged={() => void fetchProviders()}
                  onResult={setActionResult}
                />
              ))
            )}
          </div>
        </>
      )}

      <AddProviderModal open={addOpen} onOpenChange={setAddOpen} onCreated={() => void fetchProviders()} />
    </div>
  )
}
